import { useCallback, useMemo } from 'react';

import { useFragment as useApolloFragment } from '@apollo/client/react';
import { Skeleton, Stack } from '@mui/material';

import {
  type FormDefinitionType,
  RaForm,
  type RaFormOnChange,
} from '@modules/_shared/components/form/RaForm';
import type { ReportComponentProps } from '@modules/_shared/components/report-editor/ReportEditorContext';
import { getCurrencySymbol } from '@modules/_shared/utils/formatting';
import { STEP_POTENTIAL_BUYERS_FRAGMENT } from '@modules/reports/cma-reports/cmaReportsQueries';
import { FooterActions } from '@modules/reports/shared/FooterActionsComponentWithoutForm';
import { useUpdateCmaReport } from '@modules/reports/shared/utils';
import { useDebouncedHandler } from '@shared/hooks/debounce';
import { useLocale } from '@shared/hooks/locale';
import { getCurrencyByCountryCode } from '@shared/LocaleContext';

type StepPotentialBuyersForm = {
  max_budget: number | null;
  min_budget: number | null;
};

const StepPotentialBuyers = (props: ReportComponentProps) => {
  const { reportId: cmaReportId } = props;
  const { locale, countryCode } = useLocale();

  const { data, complete } = useApolloFragment({
    fragment: STEP_POTENTIAL_BUYERS_FRAGMENT,
    fragmentName: 'StepPotentialBuyers',
    from: {
      __typename: 'cma_reports',
      id: cmaReportId,
    },
  });

  const [updateCmaReport, updating] = useUpdateCmaReport(
    cmaReportId,
    'page-potential-buyers',
  );

  const initialMinBudget = useMemo(() => {
    if (data?.min_budget != null) {
      return data.min_budget === 0 ? null : data.min_budget;
    }

    if (
      data?.suggested_market_value != null &&
      data.suggested_market_value !== 0
    ) {
      return data.suggested_market_value * 0.95;
    }
    return data?.property?.latest_appraisal?.min ?? null;
  }, [
    data?.property?.latest_appraisal?.min,
    data.min_budget,
    data.suggested_market_value,
  ]);

  const initialMaxBudget = useMemo(() => {
    if (data?.max_budget != null) {
      return data.max_budget === 999_999_999 ? null : data.max_budget;
    }
    if (
      data?.suggested_market_value != null &&
      data.suggested_market_value !== 0
    ) {
      return data.suggested_market_value * 1.05;
    }
    return data?.property?.latest_appraisal?.max ?? null;
  }, [
    data?.max_budget,
    data?.suggested_market_value,
    data?.property?.latest_appraisal?.max,
  ]);

  const update = useCallback(
    async (formData?: Partial<StepPotentialBuyersForm>) => {
      await updateCmaReport({
        min_budget: formData?.min_budget,
        max_budget: formData?.max_budget,
      });
    },
    [updateCmaReport],
  );

  const debouncedUpdate = useDebouncedHandler(300, update);

  const onChangeHandler: RaFormOnChange<StepPotentialBuyersForm> = (
    formData,
    _,
    type,
  ) => {
    if (type === 'change') {
      debouncedUpdate(formData);
    }
  };

  const onSubmit = useCallback(
    async (formData: StepPotentialBuyersForm) => {
      await update(formData);
      props.setStep(props.step + 1);
    },
    [update, props],
  );

  const stepPotentialBuyersFormDefinition: FormDefinitionType<StepPotentialBuyersForm> =
    useCallback(
      ({ t }) => [
        {
          name: 'min_budget',
          type: 'number',
          prefix: getCurrencySymbol(
            getCurrencyByCountryCode(countryCode),
            locale,
          ),
          label: t('Minimum budget'),
          gridProps: { md: 12 },
          min: 0,
          max: 999_999_999,
        },
        {
          name: 'max_budget',
          type: 'number',
          prefix: getCurrencySymbol(
            getCurrencyByCountryCode(countryCode),
            locale,
          ),
          label: t('Maximum budget'),
          gridProps: { md: 12 },
          min: 0,
          max: 999_999_999,
        },
      ],
      [countryCode, locale],
    );

  return !complete ? (
    <Stack
      sx={{
        gap: 5,
      }}
    >
      <Stack direction={'row'}>
        <Skeleton variant="circular" width={24} height={24} sx={{ mr: 2 }} />
        <Skeleton variant="rounded" width={'30%'} height={24} />
      </Stack>
      <Stack
        sx={{
          gap: 4,
        }}
      >
        {Array.from({ length: 2 }).map((_, index) => (
          <Stack
            key={`budget-${index}`}
            sx={{
              gap: 1,
            }}
          >
            <Skeleton variant="rounded" height={30} width={'40%'} />
            <Skeleton variant="rounded" height={40} />
          </Stack>
        ))}
      </Stack>
    </Stack>
  ) : (
    <RaForm
      freezeInitialDefaultValues={true}
      formDefinition={stepPotentialBuyersFormDefinition}
      onSubmit={onSubmit}
      contentScrollable
      defaultValues={{
        min_budget: initialMinBudget,
        max_budget: initialMaxBudget,
      }}
      onChange={onChangeHandler}
      actionButtonsComponent={<FooterActions {...props} updating={updating} />}
    />
  );
};

export default StepPotentialBuyers;
